import { uploadTypeOf } from "./fileTypes";

/** Metadata složek, která přibalí výběr nebo přetažení složky (macOS, Windows); nejsou soubory redakce. */
const SYSTEM_FILE_NAMES: ReadonlySet<string> = new Set(["thumbs.db", "desktop.ini"]);

const isSystemFile = (name: string) =>
  name.startsWith(".") || SYSTEM_FILE_NAMES.has(name.toLowerCase());

/** Systémové podsložky (miniatury a záložní originály): `.picasaoriginals`, `.thumbnails`, `@eaDir` (Synology). */
const SYSTEM_DIRECTORY_NAMES: ReadonlySet<string> = new Set(["@eadir"]);

const isSystemDirectory = (name: string) =>
  name.startsWith(".") || SYSTEM_DIRECTORY_NAMES.has(name.toLowerCase());

/**
 * Soubor z výběru složky (`webkitdirectory`) leží v systémové podsložce. První díl cesty je
 * vybraná složka sama, poslední jméno souboru; posuzují se jen podsložky mezi nimi.
 */
const isInSystemDirectory = (file: File) =>
  (file.webkitRelativePath ?? "").split("/").slice(1, -1).some(isSystemDirectory);

/** Obrázek: typ `image/*`, nebo prázdný typ s příponou z `BATCH_EXTENSION_TYPES` (TIF, HEIC v Chrome). */
const isBatchImage = (file: File) =>
  file.type === "" ? uploadTypeOf(file) !== "" : file.type.startsWith("image/");

/**
 * Přijetí souborů do dávky: jen obrázky (`image/*` a s prázdným typem podle přípony z
 * `BATCH_EXTENSION_TYPES`); video a jiné soubory se vrátí jako odmítnuté, systémové soubory
 * složek (jméno začíná tečkou, `Thumbs.db`, `desktop.ini`) a soubory ze systémových podsložek
 * výběru složky (`isSystemDirectory`) se vynechají bez započtení.
 * Pořadí přijatých je pořadí vstupu.
 */
export function acceptBatchFiles(files: readonly File[]): { accepted: File[]; rejected: number } {
  const accepted: File[] = [];
  let rejected = 0;
  for (const file of files) {
    if (isSystemFile(file.name) || isInSystemDirectory(file)) continue;
    if (isBatchImage(file)) accepted.push(file);
    else rejected += 1;
  }
  return { accepted, rejected };
}

const fileOf = (entry: FileSystemFileEntry) =>
  new Promise<File>((resolve, reject) => entry.file(resolve, reject));

const readBatch = (reader: FileSystemDirectoryReader) =>
  new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));

/**
 * Obsah složky rekurzivně v pořadí záznamů. `readEntries` vrací po částech (Chrome nejvýš 100),
 * proto se volá, dokud nevrátí prázdné pole. Chyba čtení podsložky nebo souboru vynechá jen ji.
 * Systémové podsložky (`isSystemDirectory`) se nečtou: jejich miniatury a originály nejsou fotky redakce.
 */
async function filesOfDirectory(dir: FileSystemDirectoryEntry): Promise<File[]> {
  const reader = dir.createReader();
  const entries: FileSystemEntry[] = [];
  for (;;) {
    const batch = await readBatch(reader);
    if (batch.length === 0) break;
    entries.push(...batch);
  }
  const out: File[] = [];
  for (const entry of entries) {
    if (entry.isDirectory && isSystemDirectory(entry.name)) continue;
    out.push(...(await filesOfEntry(entry)));
  }
  return out;
}

async function filesOfEntry(entry: FileSystemEntry): Promise<File[]> {
  try {
    if (entry.isFile) return [await fileOf(entry as FileSystemFileEntry)];
    if (entry.isDirectory) return await filesOfDirectory(entry as FileSystemDirectoryEntry);
  } catch {
    // Nečitelný soubor nebo podsložka: chybí jen jejich soubory, ostatní se načtou.
  }
  return [];
}

/**
 * Soubory z přetažení včetně obsahu složek (rekurzivně), plochě a bez filtrování (filtr dělá
 * `add` přes `acceptBatchFiles`). Záznamy všech položek se berou SYNCHRONNĚ před prvním
 * `await`: `DataTransfer` po návratu z obsluhy `drop` položky zneplatní. Prohlížeč bez
 * `webkitGetAsEntry` (nebo bez `dt.items`) dá `dt.files`.
 */
export function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const items = dt.items as DataTransferItemList | undefined;
  const taken: { entry: FileSystemEntry | null; file: File | null }[] = [];
  let hasEntries = false;
  if (items) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind !== "file") continue;
      const canEntry = typeof item.webkitGetAsEntry === "function";
      hasEntries ||= canEntry;
      taken.push({ entry: canEntry ? item.webkitGetAsEntry() : null, file: item.getAsFile() });
    }
  }
  if (!hasEntries) return Promise.resolve(Array.from(dt.files ?? []));

  return (async () => {
    const out: File[] = [];
    for (const { entry, file } of taken) {
      if (entry) {
        const found = await filesOfEntry(entry);
        // Soubor, jehož záznam nejde přečíst, má ještě záložní `getAsFile()`.
        if (found.length === 0 && entry.isFile && file) out.push(file);
        else out.push(...found);
      } else if (file) {
        out.push(file);
      }
    }
    return out;
  })();
}
